import { supabase } from '../lib/supabase';

/**
 * Unread Counter Service
 * Tracks unread messages/notifications across all Community tabs
 */
export const unreadCounterService = {
  /**
   * Get unread counts for all Community tabs
   */
  async getAllUnreadCounts(userId) {
    try {
      if (!userId) return null;

      const [
        messagesCount,
        negotiationsCount,
        emailCount,
        disputesCount
      ] = await Promise.all([
        this.getDirectCommunicationCount(userId),
        this.getNegotiationsCount(userId),
        this.getEmailCount(userId),
        this.getDisputesCount(userId)
      ]);

      return {
        messages: messagesCount,
        negotiations: negotiationsCount,
        email: emailCount,
        disputes: disputesCount
      };
    } catch (error) {
      console.error('Error fetching all unread counts:', error);
      return {
        messages: 0,
        negotiations: 0,
        email: 0,
        disputes: 0
      };
    }
  },

  /**
   * Get unread message count for Direct Communication tab
   * Counts unread messages in user's negotiations
   */
  async getDirectCommunicationCount(userId) {
    try {
      // Get user's negotiations
      const { data: negotiations, error: negError } = await supabase?.from('negotiations')?.select('id')?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`);

      if (negError) throw negError;
      if (!negotiations || negotiations?.length === 0) return 0;

      const negotiationIds = negotiations?.map(n => n?.id);

      // Count unread messages in these negotiations
      const { count, error } = await supabase?.from('negotiation_messages')?.select('*', { count: 'exact', head: true })?.in('negotiation_id', negotiationIds)?.neq('sender_id', userId)?.eq('is_read', false);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching direct communication count:', error);
      return 0;
    }
  },

  /**
   * Get active negotiations count for Negotiations Dashboard tab
   * Counts negotiations with status: initiated, in_progress, or agreed
   */
  async getNegotiationsCount(userId) {
    try {
      const { count, error } = await supabase?.from('negotiations')?.select('*', { count: 'exact', head: true })?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`)?.in('status', ['initiated', 'in_progress', 'agreed']);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching negotiations count:', error);
      return 0;
    }
  },

  /**
   * Get unread email notifications count
   * This is a placeholder - email functionality doesn't have database tracking
   * Returns 0 for now, can be enhanced later
   */
  async getEmailCount(userId) {
    // Email tab doesn't have database-backed notifications yet
    // Return 0 for now
    return 0;
  },

  /**
   * Get active disputes count for Disputes tab
   * Counts disputes with status: filed, under_review, awaiting_evidence, in_mediation, or escalated
   */
  async getDisputesCount(userId) {
    try {
      const { count, error } = await supabase?.from('disputes')?.select('*', { count: 'exact', head: true })?.or(`filed_by.eq.${userId},respondent_id.eq.${userId}`)?.in('dispute_status', ['filed', 'under_review', 'awaiting_evidence', 'in_mediation', 'escalated']);

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error('Error fetching disputes count:', error);
      return 0;
    }
  },

  /**
   * Subscribe to real-time unread count updates
   * Returns cleanup function to unsubscribe
   */
  subscribeToUnreadUpdates(userId, callback) {
    if (!userId) return () => {};

    // Subscribe to negotiation_messages changes
    const messagesChannel = supabase?.channel('unread-messages-updates')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'negotiation_messages'
        },
        () => {
          // Refresh counts when messages change
          this.getAllUnreadCounts(userId)?.then(callback);
        }
      )?.subscribe();

    // Subscribe to negotiations changes
    const negotiationsChannel = supabase?.channel('negotiations-updates')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'negotiations'
        },
        () => {
          // Refresh counts when negotiations change
          this.getAllUnreadCounts(userId)?.then(callback);
        }
      )?.subscribe();

    // Subscribe to disputes changes
    const disputesChannel = supabase?.channel('disputes-updates')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'disputes'
        },
        () => {
          // Refresh counts when disputes change
          this.getAllUnreadCounts(userId)?.then(callback);
        }
      )?.subscribe();

    // Return cleanup function
    return () => {
      supabase?.removeChannel(messagesChannel);
      supabase?.removeChannel(negotiationsChannel);
      supabase?.removeChannel(disputesChannel);
    };
  },

  /**
   * Get consolidated notifications from all sources
   * Returns prioritized list with detailed information
   */
  async getConsolidatedNotifications(userId) {
    try {
      if (!userId) return [];

      const [
        messages,
        negotiations,
        generalNotifications,
        disputes
      ] = await Promise.all([
        this.getUnreadMessages(userId),
        this.getActiveNegotiations(userId),
        this.getGeneralNotifications(userId),
        this.getActiveDisputes(userId)
      ]);

      // Combine all notifications
      const allNotifications = [
        ...messages,
        ...negotiations,
        ...generalNotifications,
        ...disputes
      ];

      // Sort by priority (urgent > high > medium > low) and then by date
      const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
      
      return allNotifications?.sort((a, b) => {
        const priorityDiff = priorityOrder?.[a?.priority] - priorityOrder?.[b?.priority];
        if (priorityDiff !== 0) return priorityDiff;
        
        // If same priority, sort by date (newest first)
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
    } catch (error) {
      console.error('Error fetching consolidated notifications:', error);
      return [];
    }
  },

  /**
   * Get unread messages with details
   */
  async getUnreadMessages(userId) {
    try {
      // Get user's negotiations
      const { data: negotiations, error: negError } = await supabase?.from('negotiations')?.select('id, wish_id, initiator_id, responder_id')?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`);

      if (negError) throw negError;
      if (!negotiations || negotiations?.length === 0) return [];

      const negotiationIds = negotiations?.map(n => n?.id);

      // Get unread messages
      const { data: messages, error } = await supabase?.from('negotiation_messages')?.select(`
          *,
          sender:user_profiles!sender_id(full_name, email),
          negotiation:negotiations!negotiation_id(
            wish_id,
            initiator_id,
            responder_id
          )
        `)?.in('negotiation_id', negotiationIds)?.neq('sender_id', userId)?.eq('is_read', false)?.order('created_at', { ascending: false });

      if (error) throw error;

      return (messages || [])?.map(msg => ({
        id: `message-${msg?.id}`,
        type: 'message',
        title: `Nova poruka od ${msg?.sender?.full_name || msg?.sender?.email || 'Nepoznato'}`,
        message: msg?.message_content?.substring(0, 100) + (msg?.message_content?.length > 100 ? '...' : ''),
        createdAt: msg?.created_at,
        priority: 'medium',
        isRead: false,
        metadata: {
          messageId: msg?.id,
          negotiationId: msg?.negotiation_id,
          senderId: msg?.sender_id,
          senderName: msg?.sender?.full_name || msg?.sender?.email
        },
        actionLabel: 'Odgovori',
        actionPath: '/zajednica?tab=direct-communication'
      }));
    } catch (error) {
      console.error('Error fetching unread messages:', error);
      return [];
    }
  },

  /**
   * Get active negotiations with details
   */
  async getActiveNegotiations(userId) {
    try {
      const { data: negotiations, error } = await supabase?.from('negotiations')?.select(`
          *,
          initiator:user_profiles!initiator_id(full_name, email),
          responder:user_profiles!responder_id(full_name, email),
          wish:voucher_wishes!wish_id(
            voucher:vouchers(title)
          )
        `)?.or(`initiator_id.eq.${userId},responder_id.eq.${userId}`)?.in('status', ['initiated', 'in_progress', 'agreed'])?.order('created_at', { ascending: false });

      if (error) throw error;

      return (negotiations || [])?.map(neg => {
        const isInitiator = neg?.initiator_id === userId;
        const otherUser = isInitiator ? neg?.responder : neg?.initiator;
        
        return {
          id: `negotiation-${neg?.id}`,
          type: 'negotiation',
          title: `Pregovori sa ${otherUser?.full_name || otherUser?.email || 'Nepoznato'}`,
          message: `Vaučer: ${neg?.wish?.voucher?.title || 'Nepoznato'} - Status: ${this.translateStatus(neg?.status)}`,
          createdAt: neg?.created_at,
          priority: neg?.status === 'agreed' ? 'high' : 'medium',
          isRead: false,
          metadata: {
            negotiationId: neg?.id,
            status: neg?.status,
            otherUserId: isInitiator ? neg?.responder_id : neg?.initiator_id,
            otherUserName: otherUser?.full_name || otherUser?.email
          },
          actionLabel: 'Pogledaj',
          actionPath: '/zajednica?tab=negotiations'
        };
      });
    } catch (error) {
      console.error('Error fetching active negotiations:', error);
      return [];
    }
  },

  /**
   * Get general notifications with details
   */
  async getGeneralNotifications(userId) {
    try {
      const { data: notifications, error } = await supabase?.from('notifications')?.select(`
          *,
          related_voucher:vouchers(id, title)
        `)?.eq('user_id', userId)?.eq('is_read', false)?.order('created_at', { ascending: false });

      if (error) throw error;

      return (notifications || [])?.map(notif => ({
        id: `notification-${notif?.id}`,
        type: 'notification',
        title: notif?.title,
        message: notif?.message,
        createdAt: notif?.created_at,
        priority: 'low',
        isRead: false,
        metadata: {
          notificationId: notif?.id,
          notificationType: notif?.notification_type,
          relatedVoucherId: notif?.related_voucher_id
        },
        actionLabel: 'Vidi više',
        actionPath: notif?.related_voucher_id 
          ? `/berza?voucher=${notif?.related_voucher_id}` 
          : '/zajednica'
      }));
    } catch (error) {
      console.error('Error fetching general notifications:', error);
      return [];
    }
  },

  /**
   * Get active disputes with details
   */
  async getActiveDisputes(userId) {
    try {
      const { data: disputes, error } = await supabase?.from('disputes')?.select(`
          *,
          filed_by_user:user_profiles!filed_by(full_name, email),
          respondent_user:user_profiles!respondent_id(full_name, email)
        `)?.or(`filed_by.eq.${userId},respondent_id.eq.${userId}`)?.in('dispute_status', ['filed', 'under_review', 'awaiting_evidence', 'in_mediation', 'escalated'])?.order('filed_at', { ascending: false });

      if (error) throw error;

      return (disputes || [])?.map(dispute => {
        const isFiler = dispute?.filed_by === userId;
        const otherUser = isFiler ? dispute?.respondent_user : dispute?.filed_by_user;
        
        return {
          id: `dispute-${dispute?.id}`,
          type: 'dispute',
          title: dispute?.title,
          message: `${this.translateDisputeStatus(dispute?.dispute_status)} - ${isFiler ? 'Prijavljeno' : 'Prijavljeni'} od ${otherUser?.full_name || otherUser?.email || 'Nepoznato'}`,
          createdAt: dispute?.filed_at,
          priority: dispute?.priority === 'urgent' ? 'urgent' : dispute?.priority === 'high' ? 'high' : 'medium',
          isRead: false,
          metadata: {
            disputeId: dispute?.id,
            disputeStatus: dispute?.dispute_status,
            priority: dispute?.priority,
            isFiler
          },
          actionLabel: 'Pogledaj spor',
          actionPath: '/zajednica?tab=disputes'
        };
      });
    } catch (error) {
      console.error('Error fetching active disputes:', error);
      return [];
    }
  },

  /**
   * Translate negotiation status to Serbian
   */
  translateStatus(status) {
    const statusMap = {
      'initiated': 'Započeto',
      'in_progress': 'U toku',
      'agreed': 'Dogovoreno',
      'completed': 'Završeno',
      'cancelled': 'Otkazano'
    };
    return statusMap?.[status] || status;
  },

  /**
   * Translate dispute status to Serbian
   */
  translateDisputeStatus(status) {
    const statusMap = {
      'filed': 'Prijavljeno',
      'under_review': 'U pregledu',
      'awaiting_evidence': 'Čeka dokaze',
      'mediator_assigned': 'Dodeljen posrednik',
      'in_mediation': 'U posredovanju',
      'escalated': 'Eskalirano',
      'resolved': 'Rešeno',
      'closed': 'Zatvoreno'
    };
    return statusMap?.[status] || status;
  },

  /**
   * Mark notification as read/handled
   */
  async markAsHandled(notificationId, type) {
    try {
      if (type === 'message') {
        const messageId = notificationId?.replace('message-', '');
        const { error } = await supabase?.from('negotiation_messages')?.update({ is_read: true })?.eq('id', messageId);
        if (error) throw error;
      } else if (type === 'notification') {
        const notifId = notificationId?.replace('notification-', '');
        const { error } = await supabase?.from('notifications')?.update({ is_read: true })?.eq('id', notifId);
        if (error) throw error;
      }
      // Negotiations and disputes don't have is_read flag, so we skip them
    } catch (error) {
      console.error('Error marking as handled:', error);
      throw error;
    }
  }
};